import { useState } from 'react';
import { z } from 'zod';
import { LocalizedText, createZodFormResolver, useFormControls, Validation } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { TextKey, DataTestId, SearchType } from '@mews-commander/enums';
import { Bill, BillState } from '@mews-commander/data-access';
import { WithScope } from '@mews-commander/types';
import { Stack } from '@optimus-web/core';
import { DEFAULT_PORTAL_ID } from '@mews-commander/constants';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { getCustomer } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { EditDialogForm } from '@mews-commander/legacy-form';
import { SearchHookField } from '../../../components/form/hookFields/SearchHookField';
import { getRealBillId } from '../actions/utils';
import { EditAssociatedAccount } from './EditAssociatedAccount';
import { useOpenBillAssociationMutation } from './useOpenBillAssociationMutation';
import { useClosedBillAssociationMutation } from './useClosedBillAssociationMutation';

const CUSTOMER_SEARCH_FIELD_NAME = 'associatedAccountId';

interface AssociateBillDialogProps extends WithScope {
    active: boolean;
    bill: Bill;
    closeDialog: () => void;
}
interface FormValues {
    [CUSTOMER_SEARCH_FIELD_NAME]: string | null;
}

const searchTypes = [SearchType.Customer, SearchType.Company];

const formSchema = z.object({
    [CUSTOMER_SEARCH_FIELD_NAME]: Validation.String.Required,
});

export const AssociateBillDialog = ({ active, bill, scope, closeDialog }: AssociateBillDialogProps) => {
    const [editMode, setEditMode] = useState<'remove' | 'change'>();

    const resolver = editMode === 'remove' ? undefined : createZodFormResolver<FormValues>(formSchema);

    const dispatch = useCommanderThunkDispatch();

    const mutationFn =
        bill.state === BillState.Open ? useOpenBillAssociationMutation : useClosedBillAssociationMutation;

    const { isPending, mutate } = mutationFn();
    const onSubmit = async (values: FormValues) => {
        await dispatch(getRealBillId({ billId: bill.id, scope })).then((finalBill) => {
            mutate({ billId: finalBill, associatedAccountId: values[CUSTOMER_SEARCH_FIELD_NAME], closeDialog });
        });
    };

    const billingOwnerCompanyId = useCommanderSelector((state) => {
        const isCustomer = !isAccountIdCompany(state, { accountId: bill.ownerId });

        if (isCustomer) {
            const customer = getCustomer(state, { id: bill.ownerId });
            return customer?.companyId;
        }
    });

    const defaultValues =
        billingOwnerCompanyId && !bill.associatedAccountId
            ? { [CUSTOMER_SEARCH_FIELD_NAME]: billingOwnerCompanyId }
            : undefined;

    const { renderFormContent, submit, reset } = useFormControls<FormValues>({
        mode: 'onChange',
        onSubmit,
        resolver,
        defaultValues,
    });

    const handleCloseDialog = () => {
        closeDialog();
        reset();
    };

    return (
        <EditDialogForm
            portalContainerId={DEFAULT_PORTAL_ID}
            title={
                <LocalizedText
                    textKey={
                        bill.associatedAccountId
                            ? TextKey.AssociateBillEditDialogTitle
                            : TextKey.AssociateBillAddDialogTitle
                    }
                />
            }
            active={active}
            onClose={handleCloseDialog}
            closeDialog={handleCloseDialog}
            handleSubmit={async () => submit()}
            loading={isPending}
        >
            {renderFormContent(
                <Stack spacing="300" vertical>
                    {bill.associatedAccountId ? (
                        <EditAssociatedAccount
                            associatedAccountId={bill.associatedAccountId}
                            editMode={editMode}
                            onEditModeSelection={(selectedEditMode) => {
                                setEditMode(selectedEditMode);
                                switch (selectedEditMode) {
                                    case 'remove': {
                                        reset({ [CUSTOMER_SEARCH_FIELD_NAME]: null });
                                        break;
                                    }
                                    case 'change': {
                                        reset();
                                        break;
                                    }
                                }
                            }}
                        />
                    ) : null}
                    {!bill.associatedAccountId || editMode === 'change' ? (
                        <SearchHookField
                            name={CUSTOMER_SEARCH_FIELD_NAME}
                            searchTypes={searchTypes}
                            data-test-id={DataTestId.BillingScreenAccountSearch}
                            label={<LocalizedText textKey={TextKey.SearchForAnotherCustomerOrCompany} />}
                            placeholder={<LocalizedText textKey={TextKey.SearchByName} />}
                            bottomCaption={
                                !bill.associatedAccountId ? (
                                    <LocalizedText textKey={TextKey.AssociateBillAddAccountSearchCaption} />
                                ) : undefined
                            }
                            except={compactArray([bill.ownerId, bill.associatedAccountId])}
                            required
                            newEntity
                            autoFocus={!defaultValues}
                        />
                    ) : null}
                </Stack>
            )}
        </EditDialogForm>
    );
};
